import type { CSSProperties, ReactNode } from "react";

interface PremiumHeroProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  backgroundImage: string;
  backgroundLabel?: string;
  badges?: ReactNode;
  actions?: ReactNode;
  aside?: ReactNode;
}

const shellStyle: CSSProperties = {
  position: "relative",
  overflow: "hidden",
  borderRadius: "30px",
  border: "1px solid rgba(255,255,255,0.09)",
  boxShadow: "0 24px 64px rgba(3, 8, 20, 0.34)",
  minHeight: "min(70vh, 640px)"
};

const overlayStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(115deg, rgba(4, 9, 18, 0.94) 0%, rgba(6, 12, 22, 0.84) 46%, rgba(8, 15, 28, 0.56) 100%)"
};

const contentStyle: CSSProperties = {
  position: "relative",
  zIndex: 1,
  display: "grid",
  gap: "28px",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
  padding: "clamp(28px, 4vw, 46px)",
  alignItems: "end",
  minHeight: "min(70vh, 640px)"
};

const mediaLabelStyle: CSSProperties = {
  position: "absolute",
  right: "18px",
  bottom: "18px",
  zIndex: 2,
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 11px",
  borderRadius: "999px",
  background: "rgba(7, 12, 23, 0.78)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "rgba(236, 242, 255, 0.82)",
  fontSize: "0.7rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

export function PremiumHero({
  eyebrow,
  title,
  subtitle,
  backgroundImage,
  backgroundLabel,
  badges,
  actions,
  aside
}: PremiumHeroProps) {
  return (
    <section
      style={{
        ...shellStyle,
        backgroundImage: `linear-gradient(0deg, rgba(5, 9, 18, 0.08), rgba(5, 9, 18, 0.08)), url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div style={overlayStyle} />
      <div style={contentStyle}>
        <div className="stack-lg">
          <div className="stack-sm">
            <span
              style={{
                display: "inline-flex",
                width: "fit-content",
                padding: "8px 12px",
                borderRadius: "999px",
                background: "rgba(110, 186, 255, 0.14)",
                border: "1px solid rgba(110, 186, 255, 0.18)",
                color: "#d8ecff",
                fontSize: "0.7rem",
                letterSpacing: "0.13em",
                textTransform: "uppercase"
              }}
            >
              {eyebrow}
            </span>
            {badges ? <div className="row-wrap gap-sm" style={{ rowGap: "10px" }}>{badges}</div> : null}
            <h1
              style={{
                margin: 0,
                maxWidth: "12.5ch",
                fontSize: "clamp(2.7rem, 6vw, 5.2rem)",
                lineHeight: 0.94,
                letterSpacing: "-0.05em",
                color: "#f6fbff",
                textWrap: "balance"
              }}
            >
              {title}
            </h1>
            <p
              style={{
                margin: 0,
                maxWidth: "58ch",
                fontSize: "clamp(1rem, 1.5vw, 1.14rem)",
                lineHeight: 1.55,
                color: "rgba(231, 239, 255, 0.82)"
              }}
            >
              {subtitle}
            </p>
          </div>
          {actions ? <div className="row-wrap gap-sm" style={{ rowGap: "10px" }}>{actions}</div> : null}
        </div>

        {aside ? (
          <div
            style={{
              justifySelf: "end",
              width: "100%",
              maxWidth: "404px",
              borderRadius: "24px",
              padding: "20px",
              background: "linear-gradient(180deg, rgba(10,16,29,0.92), rgba(8,12,22,0.72))",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 18px 44px rgba(3, 8, 20, 0.28)",
              backdropFilter: "blur(18px)"
            }}
          >
            {aside}
          </div>
        ) : null}
      </div>
      {backgroundLabel ? <div style={mediaLabelStyle}>{backgroundLabel}</div> : null}
    </section>
  );
}
